import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
  Tooltip,
} from "@mui/material";
import CustomButton from "src/components/CustomButton";
import TimingSelect from "src/components/Common/TimingSelect";
import addIcon from "src/Images/Plus.svg";
import styles from "./Business.module.css";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CloseIcon from "@mui/icons-material/Close";
import DisplayVideo from "src/components/video/DisplayVideo";
import { defaultTiming, daysOfWeek } from "../../utils/constant";
import { videoExtensions } from "src/constant";
import { uploadImages } from "src/components/Common/Utils";
import { toast } from "react-toastify";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { CircularProgress } from "@mui/material";

export const MediaUploadSection = ({
  title,
  items,
  setItems,
  accept,
  allowMultiple = true,
  isDocumentSection = false,
  businessName,
  setFieldValue
}: {
  title: string;
  items: Array<string | File>;
  setItems: React.Dispatch<React.SetStateAction<Array<string | File>>>;
  accept: string;
  allowMultiple?: boolean;
  isDocumentSection?: boolean;
  businessName?: string;
  setFieldValue?: (field: string, value: any) => void;
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [useUrl, setUseUrl] = useState(false);
  const [mediaUrl, setMediaUrl] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [draggingIndex, setDraggingIndex] = useState<any>(null);

  const MAX_VIDEOS = 2;
  const MAX_PHOTOS = 15;
  const MAX_VIDEO_SIZE_MB = 30;
  const MAX_TOTAL_ITEMS = 17;

  const isVideoUrl = (url: string | File): boolean => {
    if (typeof url === "string") {
      return videoExtensions.some(ext => url.endsWith(ext));
    } else if (url instanceof File) {
      return url.type.startsWith("video/");
    }
    return false;
  };

  const countMediaTypes = () => {
    let videoCount = 0;
    let photoCount = 0;

    items.forEach(item => {
      if (isVideoUrl(item)) {
        videoCount++;
      } else {
        photoCount++;
      }
    });

    return { videoCount, photoCount };
  };

  const validateNewFiles = (newFiles: File[]) => {
    const { videoCount, photoCount } = countMediaTypes();
    let newVideoCount = 0;
    let newPhotoCount = 0;

    newFiles.forEach(file => {
      if (isVideoUrl(file)) {
        newVideoCount++;
      } else {
        newPhotoCount++;
      }
    });

    if (items.length + newFiles.length > MAX_TOTAL_ITEMS) {
      toast.error(`Maximum ${MAX_TOTAL_ITEMS} items allowed in total`);
      return false;
    }

    if (videoCount + newVideoCount > MAX_VIDEOS) {
      toast.error(`Maximum ${MAX_VIDEOS} videos allowed`);
      return false;
    }

    if (photoCount + newPhotoCount > MAX_PHOTOS) {
      toast.error(`Maximum ${MAX_PHOTOS} photos allowed`);
      return false;
    }

    for (const file of newFiles) {
      if (isVideoUrl(file) && file.size > MAX_VIDEO_SIZE_MB * 1024 * 1024) {
        toast.error(`Videos must be under ${MAX_VIDEO_SIZE_MB}MB`);
        return false;
      }
    }

    return true;
  };

  // Function to check if a resource (image or video) can be loaded
  const canLoadResource = (url: string, isVideo: boolean): Promise<boolean> => {
    return new Promise((resolve) => {
      if (isVideo) {
        const video = document.createElement("video");
        video.src = url;
        video.onloadeddata = () => resolve(true);
        video.onerror = () => resolve(false);
        video.load();
      } else {
        const img = new Image();
        img.src = url;
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
      }
    });
  };

  const handleMultipleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      if (!validateNewFiles(newFiles)) return;

      try {
        const uploadedDocuments = await uploadImages(
          newFiles,
          isDocumentSection ? "businessDocuments" : "businessGallery",
          businessName || "business"
        );
        const urls = uploadedDocuments?.uploadMultipleImages?.map((doc: any) => doc.url) || [];

        // Validate each uploaded URL to ensure it loads
        const validUrls: string[] = [];
        for (const url of urls) {
          const isVideo = isVideoUrl(url);
          const canLoad = await canLoadResource(url, isVideo);
          if (canLoad) {
            validUrls.push(url);
          } else {
            toast.error(`Failed to load ${isVideo ? "video" : "image"}: ${url}`);
          }
        }

        if (validUrls.length > 0) {
          setItems((prev) => [...prev, ...validUrls]);
          if (setFieldValue) {
            const fieldName = isDocumentSection ? "documents" : "gallery";
            setFieldValue(fieldName, [...items, ...validUrls]);
          }
        }
      } catch (error) {
        console.error("Error uploading files:", error);
        toast.error("Failed to upload files");
      }
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const newFiles = Array.from(e.dataTransfer.files);
      if (!validateNewFiles(newFiles)) return;

      try {
        const uploadedDocuments = await uploadImages(
          newFiles,
          isDocumentSection ? "businessDocuments" : "businessGallery",
          businessName || "business"
        );
        const urls = uploadedDocuments?.uploadMultipleImages?.map((doc: any) => doc.url) || [];

        // Validate each uploaded URL to ensure it loads
        const validUrls: string[] = [];
        for (const url of urls) {
          const isVideo = isVideoUrl(url);
          const canLoad = await canLoadResource(url, isVideo);
          if (canLoad) {
            validUrls.push(url);
          } else {
            toast.error(`Failed to load ${isVideo ? "video" : "image"}: ${url}`);
          }
        }

        if (validUrls.length > 0) {
          setItems((prev) => [...prev, ...validUrls]);
          if (setFieldValue) {
            const fieldName = isDocumentSection ? "documents" : "gallery";
            setFieldValue(fieldName, [...items, ...validUrls]);
          }
        }
      } catch (error) {
        console.error("Error uploading files:", error);
        toast.error("Failed to upload files");
      }
    }
  };

  const handleAddUrl = async () => {
    if (!mediaUrl) return;

    const isVideo = videoExtensions.some((ext) => mediaUrl.toLowerCase().endsWith(ext));
    const { videoCount, photoCount } = countMediaTypes();

    if (items.length >= MAX_TOTAL_ITEMS) {
      toast.error(`Maximum ${MAX_TOTAL_ITEMS} items allowed in total`);
      return;
    }

    if (isVideo && videoCount >= MAX_VIDEOS) {
      toast.error(`Maximum ${MAX_VIDEOS} videos allowed`);
      return;
    }

    if (!isVideo && photoCount >= MAX_PHOTOS) {
      toast.error(`Maximum ${MAX_PHOTOS} photos allowed`);
      return;
    }

    // Check if the URL can be loaded
    const canLoad = await canLoadResource(mediaUrl, isVideo);
    if (!canLoad) {
      toast.error(`Not Valid ${isVideo ? "video" : "image"} URL: ${mediaUrl}`);
      setMediaUrl("");
      return;
    }

    setItems((prev) => [...prev, mediaUrl]);
    if (setFieldValue) {
      const fieldName = isDocumentSection ? "documents" : "gallery";
      setFieldValue(fieldName, [...items, mediaUrl]);
    }
    setMediaUrl("");
    setUseUrl(false);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragStart = (index: any) => {
    setDraggingIndex(index);
  };

  const handleDragEnd = (event: any) => {
    event.preventDefault();
  };

  const handleOnDrop = (index: number) => {
    if (draggingIndex === null) return;

    const updatedImages = [...items];
    const [movedImage] = updatedImages.splice(draggingIndex, 1);
    updatedImages.splice(index, 0, movedImage);

    setItems(updatedImages);
    setDraggingIndex(null);
  };

  const handleRemoveItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
    if (currentImageIndex >= items.length - 1) {
      setCurrentImageIndex(Math.max(0, items.length - 2));
    }

    if (setFieldValue) {
      const fieldName = isDocumentSection ? "documents" : "gallery";
      setFieldValue(fieldName, items.filter((_, i) => i !== index));
    }
  };

  const handleItemClick = (index: number) => {
    setCurrentImageIndex(index);
  };

  const renderDocumentPreview = (item: string | File, index: number) => {
    const url = typeof item === "string" ? item : item instanceof File ? URL.createObjectURL(item) : "";
    const name = typeof item === "string" ? item.split("/").pop() || "Document" : item instanceof File ? item.name : "Document";

    if (!url) {
      return (
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="caption" color="error">
            Invalid file format
          </Typography>
        </Box>
      );
    }

    if (isDocumentSection) {
      const isPdf = typeof item === "string" ? url.toLowerCase().endsWith(".pdf") : item.type === "application/pdf";

      if (isPdf) {
        return (
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body2">{name}</Typography>
            <Typography variant="caption" color="text.secondary">
              PDF Document
            </Typography>
          </Box>
        );
      }
    }

    if (isVideoUrl(item)) {
      return <DisplayVideo item={url} />;
    }

    return (
      <img
        src={url}
        alt={name}
        style={{
          width: "100%",
          height: "auto",
          objectFit: "cover",
          borderRadius: "8px",
        }}
        onError={(e) => {
          const target = e.target as HTMLImageElement;
          target.style.display = "none";
          // Optionally, remove the item if it fails to load in preview
          handleRemoveItem(index);
        }}
      />
    );
  };

  return (
    <Box marginTop={4} mx={2}>
      <Typography variant="h6" gutterBottom>
        {title}
        <Typography variant="caption" display="block" color="textSecondary">
          {!isDocumentSection &&
            `Max ${MAX_VIDEOS} videos (${MAX_VIDEO_SIZE_MB}MB each) and ${MAX_PHOTOS} photos`}
        </Typography>
      </Typography>

      {items.length > 0 && (
        <Grid container spacing={2}>
          {items?.map((item, index) => (
            <Grid item xs={6} sm={4} md={3} key={index}>
              <Box
                position="relative"
                width="100%"
                height="auto"
                sx={{
                  borderRadius: "8px",
                  border: "2px solid #ddd ",
                  overflow: "hidden",
                  cursor: "pointer",
                }}
                onClick={() => handleItemClick(index)}
                onDragStart={() => handleDragStart(index)}
                onDragOver={handleDragEnd}
                onDrop={() => handleOnDrop(index)}
              >
                <Box
                  sx={{
                    width: "100%",
                    height: "auto",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    overflow: "hidden",
                  }}
                >
                  {renderDocumentPreview(item, index)}
                </Box>
                <IconButton
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    backgroundColor: "#ddd",
                    "&:hover": { backgroundColor: "#ddd" },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveItem(index);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {!useUrl ? (
        <Box
          sx={{
            borderRadius: 2,
            marginY: "30px",
            border: `2px dashed ${isDragging ? "#00C5B9" : "#ddd"}`,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: "pointer",
            overflow: "hidden",
            height: "100px",
            backgroundColor: isDragging ? "rgba(0, 197, 185, 0.1)" : "transparent",
            transition: "all 0.3s ease",
          }}
          onClick={() => fileInputRef.current?.click()}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            multiple={allowMultiple}
            style={{ display: "none" }}
            onChange={handleMultipleFileChange}
          />
          <Box textAlign="center">
            {isDragging ? (
              <Typography variant="body1" color="primary">
                Drop files here to upload
              </Typography>
            ) : (
              <>
                <Typography variant="body1">Drag & drop files here or click to browse</Typography>
                <Typography variant="caption" display="block" color="textSecondary">
                  Supported formats: {accept}
                </Typography>
              </>
            )}
          </Box>
        </Box>
      ) : (
        <Box sx={{ marginY: "10px" }}>
          <TextField
            label="Enter URL"
            value={mediaUrl}
            onChange={(e) => setMediaUrl(e.target.value)}
            fullWidth
            margin="normal"
            placeholder="https://example.com/image.jpg"
          />
          <CustomButton
            variant="contained"
            onClick={handleAddUrl}
            disabled={!mediaUrl.startsWith("https")}
          >
            Add URL
          </CustomButton>
        </Box>
      )}
      <Box sx={{ marginY: "20px", display: "flex", gap: 2 }}>
        <CustomButton variant={useUrl ? "outlined" : "contained"} onClick={() => setUseUrl(false)}>
          Upload File
        </CustomButton>
        <CustomButton variant={useUrl ? "contained" : "outlined"} onClick={() => setUseUrl(true)}>
          Add via URL
        </CustomButton>
      </Box>
    </Box>
  );
};

// The rest of the BusinessTiming component remains unchanged
function BusinessTiming({
  values,
  errors,
  addNewTimingField,
  timings = [],
  handleTimingChange,
  removeTimingField,
  usedDays,
  services,
  setServices,
  specialServices,
  setSpecialServices,
  setFieldValue,
  handleBack,
  loading,
  setTimings,
  mainImageUrl,
  handleMainImageChange,
  setMainImageUrl,
  setMainImageFile,
  documents,
  setDocuments,
  gallaryImages,
  setGallaryImages,
}: any) {
  const [applySameTime, setApplySameTime] = useState<boolean>(false);

  useEffect(() => {
    if (timings.length === 0) {
      setTimings([
        {
          day: "Monday",
          startTime: "09:00 AM",
          endTime: "06:00 PM",
          others: "",
        },
      ]);
    }
  }, [timings, setTimings]);

  const handleSelectForAll = (index: number) => {
    if (!timings[index]) return;

    const selectedTiming = timings[index];
    setTimings(
      timings.map((timing: any) => ({
        ...timing,
        startTime: selectedTiming.startTime,
        endTime: selectedTiming.endTime,
        others: selectedTiming.others,
      }))
    );
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setApplySameTime(e.target.checked);

    if (e.target.checked) {
      const firstTiming = timings.length > 0 ? timings[0] : defaultTiming;

      const newTimings = daysOfWeek.map((day) => ({
        day,
        startTime: firstTiming.startTime || "",
        endTime: firstTiming.endTime || "",
        others: firstTiming.others || "",
      }));

      setTimings(newTimings);
    }
  };

  const handleAddField = (
    setField: React.Dispatch<React.SetStateAction<{ value: string }[]>>,
    field: { value: string }[]
  ) => {
    setField([...field, { value: "" }]);
  };

  const handleRemoveField = (
    index: number,
    setField: React.Dispatch<React.SetStateAction<{ value: string }[]>>,
    field: { value: string }[]
  ) => {
    setField(field.filter((_, i) => i !== index));
  };

  const handleFieldChange = (
    index: number,
    value: string,
    setField: React.Dispatch<React.SetStateAction<{ value: string }[]>>,
    field: { value: string }[]
  ) => {
    const updated = [...field];
    updated[index] = { value };
    setField(updated);
  };

  return (
    <Card>
      <CardContent>
        <Typography
          variant="h6"
          gutterBottom
          fontSize={26}
          fontWeight={600}
          marginX={"40px"}
          className={styles.timing}
        >
          Business Timings
        </Typography>
        <Box className={styles.timingBox}>
          <Box display="flex" justifyContent="flex-end" alignSelf="self-end">
            <CustomButton
              type="button"
              variant="outlined"
              style={{ display: "flex", width: 200 }}
              onClick={addNewTimingField}
            >
              <Box gap={1} display={"flex"} alignItems={"center"}>
                <img src={addIcon} alt="add" height={20} width={20} />
                <span className={styles.addTiming}>Add Timing</span>
              </Box>
            </CustomButton>
          </Box>

          <Box sx={{ marginTop: "20px" }}>
            <Typography variant="h6">Timings:</Typography>
            <Box display="flex" alignItems="center" mb={2}>
              <Checkbox
                checked={applySameTime}
                onChange={handleCheckboxChange}
                inputProps={{ "aria-label": "Apply same time to all days" }}
              />
              <Typography variant="body2" ml={1}>
                Apply same timing for all days
              </Typography>
            </Box>

            {Array.isArray(timings) &&
              timings.map((timing: any, index: number) => (
                <Box key={index} mt={2}>
                  <TimingSelect
                    key={index}
                    timings={timings}
                    onTimingChange={handleTimingChange}
                    onRemove={removeTimingField}
                    availableDays={usedDays}
                    index={index}
                    other={true}
                    isBusiness={true}
                  />
                  <Box sx={{ width: "300px" }} my={1}>
                    <CustomButton
                      variant="outlined"
                      onClick={() => handleSelectForAll(index)}
                    >
                      Apply Timing to All Days
                    </CustomButton>
                  </Box>
                  {errors.timings && (
                    <Typography variant="caption" color="error" mx={1.5}>
                      {errors.timings}
                    </Typography>
                  )}
                </Box>
              ))}
          </Box>
        </Box>

        <Grid container spacing={3} mt={3}>
          <Grid item xs={6}>
            <Typography variant="h6">Services:</Typography>
            {services.map((service: { value: string }, index: number) => (
              <Box key={index} display="flex" alignItems="center" mt={2}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Enter service"
                  value={service.value}
                  onChange={(e) =>
                    handleFieldChange(index, e.target.value, setServices, services)
                  }
                />
                <IconButton
                  onClick={() => handleRemoveField(index, setServices, services)}
                  color="secondary"
                  sx={{ ml: 1 }}
                >
                  <RemoveIcon />
                </IconButton>
              </Box>
            ))}

            <Box mt={2}>
              <CustomButton
                variant="outlined"
                onClick={() => handleAddField(setServices, services)}
              >
                <AddIcon /> Add Service
              </CustomButton>
            </Box>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="h6">Special Services:</Typography>
            {specialServices.map((specialService: { value: string }, index: number) => (
              <Box key={index} display="flex" alignItems="center" mt={2}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Enter special service"
                  value={specialService.value}
                  onChange={(e) =>
                    handleFieldChange(
                      index,
                      e.target.value,
                      setSpecialServices,
                      specialServices
                    )
                  }
                />
                <IconButton
                  onClick={() =>
                    handleRemoveField(index, setSpecialServices, specialServices)
                  }
                  color="secondary"
                  sx={{ ml: 1 }}
                >
                  <RemoveIcon />
                </IconButton>
              </Box>
            ))}

            <Box mt={2}>
              <CustomButton
                variant="outlined"
                onClick={() => handleAddField(setSpecialServices, specialServices)}
              >
                <AddIcon /> Add Special Service
              </CustomButton>
            </Box>
          </Grid>
        </Grid>

        <Divider style={{ margin: "20px 0" }} />
        <Grid container spacing={2} mt={"40px"}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom fontSize={24} fontWeight={600}>
              Brand Logo
            </Typography>
            <Box className={styles.imageContainer}>
              <input
                type="file"
                onChange={handleMainImageChange}
                style={{ display: "none" }}
                id="brand-logo-upload"
              />
              <label htmlFor="brand-logo-upload">
                {mainImageUrl ? (
                  <>
                    <img
                      src={mainImageUrl}
                      alt="Business"
                      height={300}
                      width={200}
                      className={styles.imagePreview}
                    />
                    <IconButton
                      className={styles.closeIcon}
                      onClick={() => {
                        setMainImageUrl(null);
                        setMainImageFile(null);
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </>
                ) : (
                  <Box
                    sx={{
                      borderRadius: 2,
                      marginY: "20px",
                      border: "2px dashed #ddd",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      cursor: "pointer",
                      overflow: "hidden",
                      height: "80px",
                    }}
                    marginX={3}
                  >
                    <CloudUploadIcon color="primary" sx={{ marginRight: "10px" }} />
                    <Typography color="primary">Upload Brand Logo</Typography>
                  </Box>
                )}
              </label>
            </Box>
          </Grid>
        </Grid>

        <MediaUploadSection
          title="Gallery Images/Videos"
          items={gallaryImages || []}
          setItems={setGallaryImages}
          accept="image/*, video/mp4, video/webm, video/ogg, video/quicktime, video/x-matroska"
          allowMultiple={true}
          businessName={values?.businessName}
          setFieldValue={setFieldValue}
        />

        <MediaUploadSection
          title="Documents"
          items={documents || []}
          setItems={setDocuments}
          accept=".jpg,.jpeg,.png"
          allowMultiple={true}
          isDocumentSection={true}
          businessName={values?.businessName}
          setFieldValue={setFieldValue}
        />

        <Box display="flex" justifyContent="end" mt={2}>
          <Box marginX={2}>
            <CustomButton onClick={handleBack} variant="outlined">
              Back
            </CustomButton>
          </Box>
          <Box marginX={2}>
            <CustomButton
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {loading ? (
                <Box display="flex" alignItems="center" justifyContent="center">
                  <CircularProgress size={24} color="inherit" />
                </Box>
              ) : (
                "Submit"
              )}
            </CustomButton>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
export default BusinessTiming;